import { Icon } from '../../components/templates/icons';
import type { SuggestionRow } from '../../api/library-starters';

// "Suggested starters" expandable cards — sourced from library-starters API,
// inserted on click. Lifted out of DeficiencyLibraryPage.tsx during the
// 2026-05-01 split.

interface StarterSectionCardProps {
  sectionName: string;
  items: SuggestionRow[];
  expanded: boolean;
  onToggle: () => void;
  /** Currently-expanded starter row id within this section, or null. */
  expandedStarterId: string | null;
  /** Toggle a starter row's preview expansion. */
  onToggleStarter: (starterId: string) => void;
  onClone: (starterId: string) => void;
  cloningId: string | null;
}

export function StarterSectionCard({
  sectionName,
  items,
  expanded,
  onToggle,
  expandedStarterId,
  onToggleStarter,
  onClone,
  cloningId,
}: StarterSectionCardProps) {
  return (
    <div className="ts-card" style={{ marginTop: 12 }}>
      <button
        type="button"
        className="dl-starter-head"
        onClick={onToggle}
        aria-expanded={expanded}
      >
        <span className="ts-card-head-label">{sectionName}</span>
        <span className="ts-card-head-meta">
          {items.length} {items.length === 1 ? 'suggestion' : 'suggestions'}
        </span>
        <span className="ts-chev" aria-hidden="true">
          <Icon name={expanded ? 'chevDown' : 'chev'} size={16} />
        </span>
      </button>
      {expanded && (
        <div>
          {items.map((s) => (
            <StarterRow
              key={s.id}
              row={s}
              expanded={expandedStarterId === s.id}
              onToggle={() => onToggleStarter(s.id)}
              busy={cloningId === s.id}
              onClone={() => onClone(s.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function StarterRow({
  row,
  expanded,
  onToggle,
  busy,
  onClone,
}: {
  row: SuggestionRow;
  expanded: boolean;
  onToggle: () => void;
  busy: boolean;
  onClone: () => void;
}) {
  const sev = row.severity;
  const sevLabel =
    sev === 'safety_concern' ? 'Safety' : sev === 'recommendation' ? 'Rec.' : '—';
  const sevClass =
    sev === 'safety_concern'
      ? 'dl-sev-tag dl-sev-tag--safety'
      : sev === 'recommendation'
      ? 'dl-sev-tag dl-sev-tag--rec'
      : 'dl-sev-tag dl-sev-tag--none';

  const rowCls = ['ts-row', 'dl-row', 'dl-starter-row'];
  if (expanded) rowCls.push('ts-row--on');

  return (
    <>
      <div
        className={rowCls.join(' ')}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        <div className="dl-row-title">
          <span className="dl-starter-title">{row.title}</span>
          {row.componentName && (
            <span className="dl-row-component">{row.componentName}</span>
          )}
        </div>
        <span className={sevClass}>{sevLabel}</span>
        <span className="ts-chev" aria-hidden="true">
          <Icon name={expanded ? 'chevDown' : 'chev'} size={16} />
        </span>
        <div onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="ts-btn ts-btn--secondary ts-btn--sm"
            onClick={onClone}
            disabled={busy}
          >
            <Icon name="plus" size={12} /> {busy ? 'Adding…' : 'Add to library'}
          </button>
        </div>
      </div>
      {expanded && <StarterPreview row={row} />}
    </>
  );
}

// Read-only preview shown when a starter row is expanded. Mirrors the field
// layout of the owned-entry EntryDrawer so the visual rhythm is the same,
// but renders the content as plain text — starters aren't editable until
// they've been cloned into the inspector's own library.
function StarterPreview({ row }: { row: SuggestionRow }) {
  const sev = row.severity;
  const sevLabel =
    sev === 'safety_concern'
      ? 'Safety Concern'
      : sev === 'recommendation'
      ? 'Recommendation'
      : 'Observation';
  const sevPillClass =
    sev === 'safety_concern'
      ? 'dl-sev-pill dl-sev-pill--safety dl-sev-pill--on'
      : sev === 'recommendation'
      ? 'dl-sev-pill dl-sev-pill--rec dl-sev-pill--on'
      : 'dl-sev-pill dl-sev-pill--none dl-sev-pill--on';

  return (
    <div className="ts-drawer dl-drawer" onClick={(e) => e.stopPropagation()}>
      <div className="ts-drawer-head">
        <span className="ts-drawer-title">Suggestion preview</span>
        <span className="ts-drawer-meta">Not in your library yet</span>
      </div>
      <div className="dl-drawer-grid">
        {row.description && (
          <div className="dl-field">
            <span className="dl-field-label">Description</span>
            <p className="dl-field-readonly">{row.description}</p>
          </div>
        )}
        {row.recommendation && (
          <div className="dl-field">
            <span className="dl-field-label">Recommendation</span>
            <p className="dl-field-readonly">{row.recommendation}</p>
          </div>
        )}
        <div className="dl-field">
          <span className="dl-field-label">Severity</span>
          <div>
            <span className={sevPillClass}>{sevLabel}</span>
          </div>
        </div>
        {(row.componentName || row.tradeTag) && (
          <div className="dl-field">
            <span className="dl-field-label">Tags</span>
            <div className="dl-row-badges">
              {row.componentName && (
                <span className="dl-row-component">{row.componentName}</span>
              )}
              {row.tradeTag && (
                <span className="dl-row-trade">{row.tradeTag}</span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
